import { ChevronDownIcon } from '@assets/icons';
import { Button } from '@components/button';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { useLocation } from '@gatsbyjs/reach-router';
import { Organization } from '@generated/graphql';
import { useUserPreferences } from '@hooks/useUserPreferences';
import { Menu, MenuItem, useTheme } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { navigate } from 'gatsby';
import { useMemo, useState } from 'react';

export const OrganizationDisplay = () => {
  const { organization, organizations } = useOrganizationContext();
  const { hiddenOrganizationIds } = useUserPreferences();

  // Filter out hidden organizations
  const visibleOrganizations = useMemo(() => {
    if (!organizations) return [];
    return organizations.filter((org) => !hiddenOrganizationIds.includes(org.organizationId));
  }, [organizations, hiddenOrganizationIds]);

  if (visibleOrganizations?.length === 1) {
    return (
      <Typography
        variant="body2"
        sx={{ pl: 2, color: 'grey.300', textAlign: 'right' }}
        handleOverFlow
        noWrap
      >
        {organization?.name || '-'}
      </Typography>
    );
  }

  return <OrganizationSelector organizations={visibleOrganizations} organization={organization} />;
};

type OrganizationSelectorProps = {
  organization?: Pick<Organization, 'organizationId' | 'name' | 'serviceOnly'>;
  organizations?: Pick<Organization, 'organizationId' | 'name' | 'serviceOnly'>[];
};

const OrganizationSelector = ({ organizations, organization }: OrganizationSelectorProps) => {
  const { palette } = useTheme();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClose = () => setAnchorEl(null);

  const sortedOrganizations = useMemo(() => {
    if (!organizations) return [];
    return [...organizations].sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations]);

  const handleOrgSelect = (organizationId: string) => {
    if (organizationId === organization?.organizationId) {
      return;
    }
    handleClose();
    const newOrganization = organizations?.find((org) => org.organizationId === organizationId);
    if (!newOrganization) {
      return;
    }
    const pathParts = pathname.split('/');
    if (
      (Boolean(newOrganization.serviceOnly) !== Boolean(organization?.serviceOnly) &&
        !(pathParts[2] === 'volumes' || pathParts[2] === 'settings')) ||
      pathParts[2] === 'workspaces'
    ) {
      navigate(`/${organizationId}/`);
      return;
    }
    pathParts[1] = organizationId;
    navigate(`${pathParts[0]}/${organizationId}/${pathParts[2] || ''}`);
  };

  if (!organization) {
    return null;
  }

  return (
    <>
      <Button
        aria-label="Organization selector"
        sx={{
          background: 'none',
          px: 2,
          py: 1,
          border: `1px solid ${palette.header.primary}`,
          '&:hover': {
            background: 'none',
            border: `1px solid ${palette.grey[300]}`,
            '& p': { color: palette.grey[200] },
          },
          color: palette.grey[300],
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        customContent
      >
        <Group alignItems="center" gap={1}>
          {organization.name}
          <ChevronDownIcon size={16} />
        </Group>
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiMenu-paper': {
            borderTopRightRadius: 0,
          },
          // '& .MuiMenuItem-root:hover': {
          //   color: 'common.white',
          // },
          '& .MuiMenuItem-root.Mui-selected': {
            backgroundColor: 'primary.700',
            color: 'common.white',
            cursor: 'default',
            '&:hover': {
              backgroundColor: 'primary.700',
              color: 'common.white',
            },
          },
        }}
      >
        {sortedOrganizations.map((org) => (
          <MenuItem
            key={org.organizationId}
            disableRipple
            onClick={() => handleOrgSelect(org.organizationId)}
            selected={org.organizationId === organization?.organizationId}
          >
            <Typography variant="body2">{org.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
